<template>
  <Card>
    <CardHeading
      :heading="heading"
      :showCapacity="!editingUseCases"
      :currentItems="useCases.length"
      :maxItems="6"
      :showButton="!editingUseCases"
      buttonText="Add use case"
      @button-click="
        editingUseCases = {
          new: true,
          data: {
            order: useCases.length,
            title: '',
            description: '',
          },
        }
      "
    />
    <Callout v-if="useCases.length >= 6" message="Use case limit reached" />
    <div v-if="!editingUseCases && useCases[0]">
      <draggable
        v-model="useCases"
        @end="reorder($event, 'useCases')"
        v-bind="dragOptions"
        :options="{ handle: '.handle-wrapper' }"
      >
        <transition-group>
          <ProfileContentItem
            v-for="(useCase, i) in useCases"
            :key="i + 1"
            :title="useCase.title"
            :description="useCase.description"
            @edit-click="handleUseCasesEditClick(useCase)"
            @delete-click="handleDeleteItem(useCase)"
          />
        </transition-group>
      </draggable>
    </div>
    <CardText
      v-if="!editingUseCases && !useCases[0]"
      text="No use cases have been added."
    />
    <CardFormWrapper v-if="editingUseCases">
      <Input
        :value="editingUseCases.data.title"
        @input-change="handleEdit({ section: 'editingUseCases', data: $event })"
        name="title"
        label="Title"
        styles="font-weight: 400"
        class="mb-3"
        placeholder="Name this use case"
      />
      <Input
        :value="editingUseCases.data.description"
        @input-change="handleEdit({ section: 'editingUseCases', data: $event })"
        height="240px"
        type="textarea"
        :limit="500"
        name="description"
        label="Description"
        placeholder="Describe why this technology is helpful"
      />
      <div class="flex" style="justify-content: flex-end; margin-top: 2rem">
        <Button @button-click="editingUseCases = false" bType="secondary">
          Cancel
        </Button>
        <Button
          bMargin="left"
          :bDisabled="!useCaseFormComplete"
          @button-click="
            handleDoneClick({
              item: 'editingUseCases',
              section: 'useCases',
            })
          "
        >
          Done
        </Button>
      </div>
    </CardFormWrapper>
  </Card>
</template>

<script>
import Vue from 'vue';
import Callout from '@/components/DesignSystem/Callout';
import Button from '@/components/DesignSystem/Button';
import Card from '@/components/DesignSystem/Card';
import Input from '@/components/DesignSystem/Input';
import CardHeading from '@/components/DesignSystem/CardHeading';
import CardText from '@/components/DesignSystem/CardText';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';
import draggable from 'vuedraggable';

export default {
  components: {
    Button,
    draggable,
    Card,
    Input,
    Callout,
    CardHeading,
    CardText,
    CardFormWrapper,
    ProfileContentItem,
  },
  props: {
    namespace: {
      type: String,
    },
    heading: {
      type: String,
      default: 'Use Cases',
    },
  },
  data() {
    return {
      editingUseCases: false,
    };
  },
  computed: {
    useCaseFormComplete() {
      if (this.editingUseCases && this.editingUseCases.data) {
        if (
          this.editingUseCases.data.title &&
          this.editingUseCases.data.description
        ) {
          return true;
        }
      }
      return false;
    },
    useCases: {
      get() {
        return this.$store.getters[`${this.namespace}/useCases`].filter(
          i => !i.delete
        );
      },
      set(value) {
        this.$store.commit(`${this.namespace}/setUseCases`, value);
      },
    },
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
      };
    },
  },
  methods: {
    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      this.$forceUpdate();
    },
    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addItemForUseCases({ section, data: this[item]['data'] });
      } else {
        this.editItemForUseCases({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },
    addItemForUseCases(payload) {
      this.$store.dispatch(`${this.namespace}/addItemForUseCases`, payload);
    },
    editItemForUseCases(payload) {
      this.$store.dispatch(`${this.namespace}/editItemForUseCases`, payload);
    },
    updateError(payload) {
      this.$store.dispatch(`${this.namespace}/updateError`, payload);
    },
    handleUseCasesEditClick(useCase) {
      this.editingUseCases = {
        new: false,
        data: { ...useCase },
      };
    },
    reorder(event, section) {
      this[section] = this[section].map((item, i) => {
        return {
          ...item,
          order: i,
        };
      });
      this.findItemsToUpdate(event, section);
      this.$store.dispatch(`${this.namespace}/addDirtyTab`, {
        endpoint: 'use_cases/',
        storeKey: section,
      });
    },
    findItemsToUpdate(event, section) {
      const start = event.oldIndex;
      const end = event.newIndex;

      if (start < end) {
        for (let i = start; i <= end; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      } else {
        for (let i = end; i <= start; i++) {
          if (this[section][i]['id']) {
            this.$set(this[section][i], 'update', true);
          }
        }
      }
    },
    handleDeleteItem(useCase) {
      const payload = {
        item: useCase,
        section: 'useCases',
        modalTitle: 'Delete use cases?',
        modalDescription:
          'This action cannot be undone, are you sure you want to delete this item?',
      };
      this.$emit('delete-content', payload);
    },
  },
};
</script>
